import React from 'react';
import { detectFormat } from '../utils/payloadFormat';

// Lightweight, dependency-free syntax highlighting for the pretty-printed payloads.
// Tokenizes the already-normalized text into colored <span>s. Whitespace/newlines are
// preserved by the surrounding <pre>.

const COLORS = {
  punctuation: '#57606a',
  tag: '#0550ae',
  attrName: '#8250df',
  value: '#0a7d33',
  number: '#0b6bcb',
  keyword: '#953800',
};

const renderTag = (tag: string): React.ReactNode[] => {
  const spans: React.ReactNode[] = [];
  const inner = tag.slice(1, -1);
  const isClose = inner.startsWith('/');
  const isSelfClose = inner.endsWith('/');

  let body = inner;
  if (isClose) body = body.slice(1);
  if (isSelfClose) body = body.slice(0, -1);

  const name = (body.match(/^\S+/) || [body])[0];
  const rest = body.slice(name.length);

  spans.push(<span key="lt" style={{ color: COLORS.punctuation }}>{isClose ? '</' : '<'}</span>);
  spans.push(<span key="nm" style={{ color: COLORS.tag, fontWeight: 600 }}>{name}</span>);

  const attrRegex = /([\w:.-]+)(\s*=\s*)("[^"]*"|'[^']*')/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = attrRegex.exec(rest)) !== null) {
    if (m.index > last) spans.push(<span key={`ws${i}`}>{rest.slice(last, m.index)}</span>);
    spans.push(<span key={`an${i}`} style={{ color: COLORS.attrName }}>{m[1]}</span>);
    spans.push(<span key={`eq${i}`} style={{ color: COLORS.punctuation }}>{m[2]}</span>);
    spans.push(<span key={`av${i}`} style={{ color: COLORS.value }}>{m[3]}</span>);
    last = m.index + m[0].length;
    i++;
  }
  if (last < rest.length) spans.push(<span key="tail">{rest.slice(last)}</span>);
  spans.push(<span key="gt" style={{ color: COLORS.punctuation }}>{isSelfClose ? '/>' : '>'}</span>);
  return spans;
};

const renderXml = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  text.split(/(<[^>]+>)/g).forEach((part, i) => {
    if (!part) return;
    if (part.startsWith('<') && part.endsWith('>')) {
      nodes.push(<React.Fragment key={i}>{renderTag(part)}</React.Fragment>);
    } else {
      nodes.push(<span key={i}>{part}</span>);
    }
  });
  return nodes;
};

const renderJson = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const regex = /("(?:[^"\\]|\\.)*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={`t${i}`}>{text.slice(last, m.index)}</span>);
    if (m[1] !== undefined) {
      if (m[2]) {
        nodes.push(<span key={`k${i}`} style={{ color: COLORS.tag, fontWeight: 600 }}>{m[1]}</span>);
        nodes.push(<span key={`c${i}`} style={{ color: COLORS.punctuation }}>{m[2]}</span>);
      } else {
        nodes.push(<span key={`s${i}`} style={{ color: COLORS.value }}>{m[1]}</span>);
      }
    } else if (m[3] !== undefined) {
      nodes.push(<span key={`kw${i}`} style={{ color: COLORS.keyword }}>{m[3]}</span>);
    } else if (m[4] !== undefined) {
      nodes.push(<span key={`n${i}`} style={{ color: COLORS.number }}>{m[4]}</span>);
    } else if (m[5] !== undefined) {
      nodes.push(<span key={`p${i}`} style={{ color: COLORS.punctuation }}>{m[5]}</span>);
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) nodes.push(<span key="end">{text.slice(last)}</span>);
  return nodes;
};

const HighlightedCode: React.FC<{ text: string }> = ({ text }) => {
  const format = detectFormat(text);
  const content = format === 'xml' ? renderXml(text) : format === 'json' ? renderJson(text) : text;
  return <pre className="mb-0">{content}</pre>;
};

export default HighlightedCode;
